import React, { useEffect, useMemo, useState } from "react";
import { Button, Card, Col, Form, Modal, Row, Spinner, Tab, Table, Tabs } from "react-bootstrap";
import Select from "react-select";
import { toast } from "react-toastify";
import Header from "../../layouts/Header";
import axiosInstance, { opsAxiosInstance } from "../../api/axios";
// Reusable outlet dropdown (grouped Active / Inactive / Coming Soon).
// Adjust the path below to wherever OutletSelect actually lives in your tree.
import OutletSelect from "../../components/OutletSelect";

// Hardcoded per request. Note: since this ships inside the frontend bundle,
// it is visible to anyone via browser dev tools / the network tab — treat
// it as public, not secret, and make sure this route is otherwise gated
// (VPN, login wall, etc.) if that matters for this deployment.
const API_KEY = "sYQ8SawD0mdqOtPO18NGs1paiEccyXJpFzsCiz1wXgC";
const API_SECRET = "79SvSQbGhcgCK9GdKTN61xTjiJvelIJVkC7pY7ioPaAOryyqbjy4pir2ckssjoCD";

// Attached to every admin request below.
const ADMIN_HEADERS = {
    headers: {
        "X-API-Key": API_KEY,
        "X-API-Secret": API_SECRET,
    },
};

const initialOutletForm = {
    outlet_name: "",
    outlet_code: "",
    address: "",
    city: "",
    state: "",
    latitude: "",
    longitude: "",
    is_active: '1',
};

const initialStaffForm = {
    outlet_id: "",
    staff_name: "",
    mobile_number: "",
    is_active: '1',
};

const initialCategoryForm = {
    category_name: "",
    checklist_type: "OPENING",
    frequency: "DAILY",
    is_active: '1',
    sort_order: "",
};

const initialTaskForm = {
    category_id: "",
    task_name: "",
    is_mandatory: '1',
    is_active: '1',
    sort_order: "",
};

const initialAssignmentForm = {
    outlet_id: "",
    category_id: "",
    is_active: '1',
};

const SECTION_LABEL = {
    outlets: "outlet",
    staff: "staff member",
    categories: "category",
    tasks: "task",
    assignments: "assignment",
};

const OUTLET_PAGE_SIZE = 12;

const normalizeData = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.data?.data)) return payload.data.data;
    if (Array.isArray(payload?.data?.items)) return payload.data.items;
    return [];
};

// Maps a "main outlet" record (from getMainOutletsData / the `outlet` API)
// onto the checklist-admin outlet form shape. Field names on the left are
// based on what OutletSelect itself reads (_id, name, city, status) — if
// your outlet API returns address/state/lat/lng under different keys,
// adjust the right-hand side accordingly.
const mapMainOutletToForm = (outlet) => ({
    outlet_name: outlet?.name || outlet?.outlet_name || "",
    outlet_code: outlet?.code || outlet?.outlet_code || "",
    address: outlet?.address || "",
    city: outlet?.city || "",
    state: outlet?.state || "",
    latitude: outlet?.coordinates?.latitude ?? outlet?.lat ?? "",
    longitude: outlet?.coordinates?.longitude ?? outlet?.lng ?? "",
    is_active: outlet?.status === "ACTIVE" ? "1" : "0",
});

// Maps an "outlet user" record (from getOutletUsers / the `outlet/user` API)
// onto the checklist-admin staff form shape. Field names on the left are a
// best guess (name, mobile_number, outlet_id, status) — verify against the
// real outletUsers response and adjust as needed.
const mapOutletUserToForm = (user) => ({
    outlet_id: user?.outlet_id || user?.outletId || "",
    staff_name: user?.name || user?.staff_name || user?.full_name || "",
    mobile_number: user?.mobile_number || user?.mobile || user?.phone || "",
    is_active: user?.status === "ACTIVE" || user?.is_active === 1 || user?.is_active === "1" ? "1" : "0",
});

// Minimal react-select styling, reused for the "prefill from existing
// outlet user" dropdown in the Add Staff modal. Kept local (rather than
// importing from OutletSelect) since that file doesn't export its styles.
const staffSelectStyles = {
    control: (provided, state) => ({
        ...provided,
        borderColor: state.isFocused ? "#86b7fe" : "#c5cbd1",
        minHeight: "38px",
        boxShadow: state.isFocused ? "0 0 0 0.25rem rgba(13,110,253,.25)" : "none",
        "&:hover": { borderColor: "#6c757d" },
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    option: (provided, state) => ({
        ...provided,
        fontSize: "0.875rem",
        backgroundColor: state.isSelected ? "#0d6efd" : state.isFocused ? "#e8f0fe" : "white",
        color: state.isSelected ? "white" : "#212529",
        cursor: "pointer",
    }),
};

// Small monochrome status indicator instead of a colored badge pill.
const StatusTag = ({ active }) => (
    <span className="co-status">
        <span className={`co-dot ${active ? "is-active" : "is-inactive"}`} />
        {active ? "Active" : "Inactive"}
    </span>
);

// Flat, compact action links used in every table row.
const RowActions = ({ onEdit, onToggle, onDelete, isActive }) => (
    <div className="co-row-actions">
        <button type="button" className="co-link" onClick={onEdit}>Edit</button>
        <span className="co-sep">·</span>
        <button type="button" className="co-link" onClick={onToggle}>{isActive ? "Disable" : "Enable"}</button>
        <span className="co-sep">·</span>
        <button type="button" className="co-link co-link-danger" onClick={onDelete}>Delete</button>
    </div>
);

const ChecklistOperations = () => {

    const accessToken = localStorage.getItem("accessToken");
    const [activeTab, setActiveTab] = useState("outlets");
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [outlets, setOutlets] = useState([]);
    const [staff, setStaff] = useState([]);
    const [categories, setCategories] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [assignments, setAssignments] = useState([]);

    const [outletForm, setOutletForm] = useState(initialOutletForm);
    const [staffForm, setStaffForm] = useState(initialStaffForm);
    const [categoryForm, setCategoryForm] = useState(initialCategoryForm);
    const [taskForm, setTaskForm] = useState(initialTaskForm);
    const [assignmentForm, setAssignmentForm] = useState(initialAssignmentForm);

    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState("create");
    const [modalSection, setModalSection] = useState("outlets");
    const [activeItemId, setActiveItemId] = useState(null);

    // Confirm-delete modal state — replaces window.confirm with something
    // that actually matches the rest of the UI and names what's being removed.
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteBusy, setDeleteBusy] = useState(false);

    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    // Kept separate on purpose: reusing one filter value for both the staff
    // tab (filters by outlet_id) and the tasks tab (filters by category_id)
    // meant switching tabs silently applied the wrong filter, since the two
    // id spaces can overlap.
    const [staffOutletFilter, setStaffOutletFilter] = useState("all");
    const [taskCategoryFilter, setTaskCategoryFilter] = useState("all");

    // Outlet cards are paginated client-side so a deployment with ~100
    // outlets doesn't render/scroll all of them at once.
    const [outletPage, setOutletPage] = useState(1);

    const [internalOutlets, setInternalOutlets] = useState([]);

    // Currently selected "existing outlet" chosen in the Add Outlet modal,
    // used purely to prefill outletForm. Not submitted to the API directly.
    const [selectedMainOutlet, setSelectedMainOutlet] = useState(null);
    const [internalOutletUsers, setInternalOutletUsers] = useState([]);

    // Currently selected "existing outlet user" chosen in the Add Staff
    // modal, used purely to prefill staffForm. Not submitted directly.
    const [selectedMainStaff, setSelectedMainStaff] = useState(null);

    useEffect(() => {
        loadAllData();
        getMainOutletsData();
        getOutletUsers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Reset filters whenever the tab changes so state from one tab never
    // silently leaks into another.
    useEffect(() => {
        setSearchTerm("");
        setStatusFilter("all");
        setStaffOutletFilter("all");
        setTaskCategoryFilter("all");
        setOutletPage(1);
    }, [activeTab]);

    // Jump back to page 1 whenever the outlet search/status filter changes,
    // so a filter that narrows the list doesn't leave the user stranded on
    // a now-empty page.
    useEffect(() => {
        setOutletPage(1);
    }, [searchTerm, statusFilter]);

    const getMainOutletsData = async () => {
        try {
            const headers = {
                Authorization: `Bearer ${accessToken}`
            }
            const response = await axiosInstance.get('outlet', { headers })
            if (response?.data?.status === true) {
                setInternalOutlets(response.data.data.outlets);
            }
        } catch (error) {
            if (error?.response) {
                const status = error?.response?.status;
                let errorMessage = status === 406
                    ? error?.response?.data?.error?.message || error?.response?.data?.error
                    : status === 412
                        ? error?.response?.data?.data?.message
                        : error?.response?.data?.error || 'Failed to fetch outlets';
                toast.error(errorMessage, { position: 'top-right', autoClose: 3000, theme: 'colored' });
            }
        }
    }

    const getOutletUsers = async () => {
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const response = await axiosInstance.get(`outlet/user`, { headers });
            if (response?.data?.status === true) {
                const users = response?.data?.data?.outletUsers;
                setInternalOutletUsers(users);
            }
        } catch (error) {
            if (error.response?.data?.status === false)
                toast.error(error.response.data.error);
        }
    };

    const loadAllData = async () => {
        setLoading(true);

        try {
            const requests = [
                opsAxiosInstance.get("/api/admin/outlets", ADMIN_HEADERS),
                opsAxiosInstance.get("/api/admin/staff", ADMIN_HEADERS),
                opsAxiosInstance.get("/api/admin/checklist-categories", ADMIN_HEADERS),
                opsAxiosInstance.get("/api/admin/checklist-tasks", ADMIN_HEADERS),
                opsAxiosInstance.get("/api/admin/outlet-checklist-categories", ADMIN_HEADERS),
            ];

            const [outletsRes, staffRes, categoriesRes, tasksRes, assignmentsRes] = await Promise.all(requests);

            setOutlets(normalizeData(outletsRes?.data));
            setStaff(normalizeData(staffRes?.data));
            setCategories(normalizeData(categoriesRes?.data));
            setTasks(normalizeData(tasksRes?.data));
            setAssignments(normalizeData(assignmentsRes?.data));
        } catch (error) {
            const message = error?.response?.data?.message || "Unable to load checklist admin data.";
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setOutletForm(initialOutletForm);
        setStaffForm(initialStaffForm);
        setCategoryForm(initialCategoryForm);
        setTaskForm(initialTaskForm);
        setAssignmentForm(initialAssignmentForm);
        setActiveItemId(null);
        setSelectedMainOutlet(null);
        setSelectedMainStaff(null);
    };

    const openCreateModal = (section) => {
        resetForm();
        setModalSection(section);
        setModalMode("create");
        setShowModal(true);
    };

    // Same as openCreateModal, but pre-fills the outlet so adding staff or
    // assigning a category from an outlet card doesn't make the user pick
    // the outlet a second time.
    const openAddStaffForOutlet = (outlet) => {
        resetForm();
        setStaffForm({ ...initialStaffForm, outlet_id: outlet?.id || outlet?._id });
        setModalSection("staff");
        setModalMode("create");
        setShowModal(true);
    };

    const openAddAssignmentForOutlet = (outlet) => {
        resetForm();
        setAssignmentForm({ ...initialAssignmentForm, outlet_id: outlet?.id || outlet?._id });
        setModalSection("assignments");
        setModalMode("create");
        setShowModal(true);
    };

    // Tasks belong to a category, not directly to an outlet, so this
    // pre-fills the category with the outlet's first assigned category
    // when there is exactly one unambiguous choice — otherwise it leaves
    // the category picker for the admin to fill in.
    const openAddTaskForOutlet = (outlet) => {
        resetForm();
        const outletId = outlet?.id || outlet?._id;
        const outletAssignments = assignmentsByOutletId[String(outletId)] || [];
        const uniqueCategoryIds = [...new Set(outletAssignments.map((a) => String(a?.category_id)))];
        const defaultCategoryId = uniqueCategoryIds.length === 1 ? uniqueCategoryIds[0] : "";

        if (outletAssignments.length === 0) {
            toast.warning("This outlet has no assigned categories yet. Assign one first, or pick a category manually.");
        }

        setTaskForm({ ...initialTaskForm, category_id: defaultCategoryId });
        setModalSection("tasks");
        setModalMode("create");
        setShowModal(true);
    };

    const openEditModal = (section, item) => {
        setModalSection(section);
        setModalMode("edit");
        setActiveItemId(item?.id || item?._id);

        if (section === "outlets") {
            setOutletForm({
                outlet_name: item?.outlet_name || "",
                outlet_code: item?.outlet_code || "",
                address: item?.address || "",
                city: item?.city || "",
                state: item?.state || "",
                latitude: item?.latitude ?? "",
                longitude: item?.longitude ?? "",
                is_active: item?.is_active ?? 1,
            });
        }

        if (section === "staff") {
            setStaffForm({
                outlet_id: item?.outlet_id || "",
                staff_name: item?.staff_name || "",
                mobile_number: item?.mobile_number || "",
                is_active: item?.is_active ?? 1,
            });
        }

        if (section === "categories") {
            setCategoryForm({
                category_name: item?.category_name || "",
                checklist_type: item?.checklist_type || "OPENING",
                frequency: item?.frequency || "DAILY",
                is_active: item?.is_active ?? 1,
                sort_order: item?.sort_order ?? "",
            });
        }

        if (section === "tasks") {
            setTaskForm({
                category_id: item?.category_id || "",
                task_name: item?.task_name || "",
                is_mandatory: item?.is_mandatory ?? 1,
                is_active: item?.is_active ?? 1,
                sort_order: item?.sort_order ?? "",
            });
        }

        if (section === "assignments") {
            setAssignmentForm({
                outlet_id: item?.outlet_id || "",
                category_id: item?.category_id || "",
                is_active: item?.is_active ?? 1,
            });
        }

        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        resetForm();
    };

    // Fires when the admin picks an outlet from the "prefill from existing
    // outlet" dropdown inside the Add Outlet modal. Looks up the full record
    // in internalOutlets (fetched by getMainOutletsData) and maps it onto
    // outletForm. Clearing the select resets the form back to blank.
    const handleSelectMainOutlet = (selectedOption) => {
        setSelectedMainOutlet(selectedOption);

        if (!selectedOption) {
            setOutletForm(initialOutletForm);
            return;
        }

        const matched = internalOutlets.find((o) => o._id === selectedOption.value);
        if (matched) {
            setOutletForm(mapMainOutletToForm(matched));
        } else {
            toast.warning("Couldn't find full details for that outlet — please fill the form in manually.");
        }
    };

    // Fires when the admin picks a user from the "prefill from existing
    // outlet user" react-select dropdown inside the Add Staff modal. Looks
    // up the full record in internalOutletUsers (fetched by getOutletUsers)
    // and maps it onto staffForm. Clearing the select resets the form.
    const handleSelectMainStaff = (selectedOption) => {
        setSelectedMainStaff(selectedOption);

        if (!selectedOption) {
            setStaffForm(initialStaffForm);
            return;
        }

        const matched = internalOutletUsers.find(
            (u) => (u._id || u.id) === selectedOption.value
        );
        if (matched) {
            setStaffForm(mapOutletUserToForm(matched));
        } else {
            toast.warning("Couldn't find full details for that user — please fill the form in manually.");
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setSaving(true);

        try {
            let payload = {};
            let endpoint = "";
            let method = "post";

            if (modalSection === "outlets") {
                payload = {
                    outlet_name: outletForm.outlet_name,
                    outlet_code: outletForm.outlet_code || undefined,
                    address: outletForm.address || undefined,
                    city: outletForm.city || undefined,
                    state: outletForm.state || undefined,
                    latitude: outletForm.latitude ? Number(outletForm.latitude) : undefined,
                    longitude: outletForm.longitude ? Number(outletForm.longitude) : undefined,
                    is_active: Number(outletForm.is_active),
                };
                endpoint = modalMode === "edit" ? `/api/admin/outlets/${activeItemId}` : "/api/admin/outlets";
                method = modalMode === "edit" ? "put" : "post";
            }

            if (modalSection === "staff") {
                payload = {
                    outlet_id: Number(staffForm.outlet_id),
                    staff_name: staffForm.staff_name,
                    mobile_number: staffForm.mobile_number,
                    is_active: Number(staffForm.is_active),
                };
                endpoint = modalMode === "edit" ? `/api/admin/staff/${activeItemId}` : "/api/admin/staff";
                method = modalMode === "edit" ? "put" : "post";
            }

            if (modalSection === "categories") {
                payload = {
                    category_name: categoryForm.category_name,
                    checklist_type: categoryForm.checklist_type,
                    frequency: categoryForm.frequency,
                    is_active: Number(categoryForm.is_active),
                    sort_order: categoryForm.sort_order ? Number(categoryForm.sort_order) : undefined,
                };
                endpoint = modalMode === "edit" ? `/api/admin/checklist-categories/${activeItemId}` : "/api/admin/checklist-categories";
                method = modalMode === "edit" ? "put" : "post";
            }

            if (modalSection === "tasks") {
                payload = {
                    category_id: Number(taskForm.category_id),
                    task_name: taskForm.task_name,
                    is_mandatory: Number(taskForm.is_mandatory),
                    is_active: Number(taskForm.is_active),
                    sort_order: taskForm.sort_order ? Number(taskForm.sort_order) : undefined,
                };
                endpoint = modalMode === "edit" ? `/api/admin/checklist-tasks/${activeItemId}` : "/api/admin/checklist-tasks";
                method = modalMode === "edit" ? "put" : "post";
            }

            if (modalSection === "assignments") {
                payload = {
                    outlet_id: Number(assignmentForm.outlet_id),
                    category_id: Number(assignmentForm.category_id),
                    is_active: Number(assignmentForm.is_active),
                };
                endpoint = modalMode === "edit" ? `/api/admin/outlet-checklist-categories/${activeItemId}` : "/api/admin/outlet-checklist-categories";
                method = modalMode === "edit" ? "put" : "post";
            }

            await opsAxiosInstance[method](endpoint, payload, ADMIN_HEADERS);
            toast.success(`Saved ${modalSection} successfully.`);
            closeModal();
            loadAllData();
        } catch (error) {
            const message = error?.response?.data?.message || error?.response?.data?.errors?.[0] || "Unable to save the selected checklist item.";
            toast.error(message);
        } finally {
            setSaving(false);
        }
    };

    // Human-readable label for the confirm-delete dialog, per section.
    const getItemLabel = (section, item) => {
        if (section === "outlets") return item?.outlet_name || "this outlet";
        if (section === "staff") return item?.staff_name || "this staff member";
        if (section === "categories") return item?.category_name || "this category";
        if (section === "tasks") return item?.task_name || "this task";
        if (section === "assignments") {
            const parts = [item?.category_name, item?.outlet_name].filter(Boolean);
            return parts.length ? `${parts[0]}${parts[1] ? ` for ${parts[1]}` : ""}` : "this assignment";
        }
        return "this item";
    };

    // Opens the confirm-delete modal instead of deleting immediately.
    // Takes the full item (not just its id) so the dialog can name what's
    // about to be removed.
    const requestDelete = (section, item) => {
        setDeleteTarget({
            section,
            id: item?.id || item?._id,
            label: getItemLabel(section, item),
        });
    };

    const cancelDelete = () => {
        if (deleteBusy) return;
        setDeleteTarget(null);
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        const { section, id } = deleteTarget;
        setDeleteBusy(true);

        try {
            let endpoint = "";
            if (section === "outlets") endpoint = `/api/admin/outlets/${id}`;
            if (section === "staff") endpoint = `/api/admin/staff/${id}`;
            if (section === "categories") endpoint = `/api/admin/checklist-categories/${id}`;
            if (section === "tasks") endpoint = `/api/admin/checklist-tasks/${id}`;
            if (section === "assignments") endpoint = `/api/admin/outlet-checklist-categories/${id}`;

            await opsAxiosInstance.delete(endpoint, ADMIN_HEADERS);
            toast.success("Removed successfully.");
            setDeleteTarget(null);
            loadAllData();
        } catch (error) {
            const message = error?.response?.data?.message || "Unable to delete this item.";
            toast.error(message);
        } finally {
            setDeleteBusy(false);
        }
    };


    const toggleStatus = async (section, item) => {
        const targetId = item?.id || item?._id;
        const nextValue = item?.is_active === '1' ? '0' : '1';

        try {
            let endpoint = "";
            const payload = { is_active: nextValue };

            if (section === "outlets") endpoint = `/api/admin/outlets/${targetId}`;
            if (section === "staff") endpoint = `/api/admin/staff/${targetId}`;
            if (section === "categories") endpoint = `/api/admin/checklist-categories/${targetId}`;
            if (section === "tasks") endpoint = `/api/admin/checklist-tasks/${targetId}`;
            if (section === "assignments") endpoint = `/api/admin/outlet-checklist-categories/${targetId}`;

            await opsAxiosInstance.put(endpoint, payload, ADMIN_HEADERS);
            toast.success("Status updated.");
            loadAllData();
        } catch (error) {
            const message = error?.response?.data?.message || "Unable to update status.";
            toast.error(message);
        }
    };

    const filteredOutlets = useMemo(() => {
        return outlets.filter((item) => {
            const matchesSearch = [item?.outlet_name, item?.outlet_code, item?.city].filter(Boolean).join(" ").toLowerCase().includes(searchTerm.toLowerCase());
            const matchesStatus = statusFilter === "all" ? true : String(item?.is_active) === statusFilter;
            return matchesSearch && matchesStatus;
        });
    }, [outlets, searchTerm, statusFilter]);

    const totalOutletPages = Math.max(1, Math.ceil(filteredOutlets.length / OUTLET_PAGE_SIZE));
    const safeOutletPage = Math.min(outletPage, totalOutletPages);
    const pagedOutlets = useMemo(() => {
        const start = (safeOutletPage - 1) * OUTLET_PAGE_SIZE;
        return filteredOutlets.slice(start, start + OUTLET_PAGE_SIZE);
    }, [filteredOutlets, safeOutletPage]);

    const filteredStaff = useMemo(() => {
        return staff.filter((item) => {
            const matchesSearch = [item?.staff_name, item?.mobile_number, item?.outlet_name].filter(Boolean).join(" ").toLowerCase().includes(searchTerm.toLowerCase());
            const matchesStatus = statusFilter === "all" ? true : String(item?.is_active) === statusFilter;
            const matchesOutlet = staffOutletFilter === "all" ? true : String(item?.outlet_id) === staffOutletFilter;
            return matchesSearch && matchesStatus && matchesOutlet;
        });
    }, [staff, searchTerm, statusFilter, staffOutletFilter]);

    const filteredCategories = useMemo(() => {
        return categories.filter((item) => {
            const matchesSearch = [item?.category_name, item?.checklist_type, item?.frequency].filter(Boolean).join(" ").toLowerCase().includes(searchTerm.toLowerCase());
            const matchesStatus = statusFilter === "all" ? true : String(item?.is_active) === statusFilter;
            return matchesSearch && matchesStatus;
        });
    }, [categories, searchTerm, statusFilter]);

    const filteredTasks = useMemo(() => {
        return tasks.filter((item) => {
            const matchesSearch = [item?.task_name, item?.category_name].filter(Boolean).join(" ").toLowerCase().includes(searchTerm.toLowerCase());
            const matchesStatus = statusFilter === "all" ? true : String(item?.is_active) === statusFilter;
            const matchesCategory = taskCategoryFilter === "all" ? true : String(item?.category_id) === taskCategoryFilter;
            return matchesSearch && matchesStatus && matchesCategory;
        });
    }, [tasks, searchTerm, statusFilter, taskCategoryFilter]);

    const filteredAssignments = useMemo(() => {
        return assignments.filter((item) => {
            const matchesSearch = [item?.outlet_name, item?.category_name].filter(Boolean).join(" ").toLowerCase().includes(searchTerm.toLowerCase());
            const matchesStatus = statusFilter === "all" ? true : String(item?.is_active) === statusFilter;
            return matchesSearch && matchesStatus;
        });
    }, [assignments, searchTerm, statusFilter]);

    // Group staff/categories/tasks by outlet so each outlet card can show its
    // own slice of every resource without re-fetching anything.
    const staffByOutlet = useMemo(() => {
        const map = {};
        staff.forEach((item) => {
            const key = String(item?.outlet_id);
            if (!map[key]) map[key] = [];
            map[key].push(item);
        });
        return map;
    }, [staff]);

    const assignmentsByOutletId = useMemo(() => {
        const map = {};
        assignments.forEach((item) => {
            const key = String(item?.outlet_id);
            if (!map[key]) map[key] = [];
            map[key].push(item);
        });
        return map;
    }, [assignments]);

    const tasksByCategoryId = useMemo(() => {
        const map = {};
        tasks.forEach((item) => {
            const key = String(item?.category_id);
            if (!map[key]) map[key] = [];
            map[key].push(item);
        });
        return map;
    }, [tasks]);

    const categoryById = useMemo(() => {
        const map = {};
        categories.forEach((item) => {
            map[String(item?.id ?? item?._id)] = item;
        });
        return map;
    }, [categories]);

    // Options for the "prefill from existing outlet user" react-select in
    // the Add Staff modal, grouped by outlet so it's easy to scan when the
    // same person appears at multiple outlets. Adjust the field names below
    // (name / mobile_number / outlet_name) once the real outletUsers shape
    // is confirmed.
    const staffUserOptions = useMemo(() => {
        const groups = {};
        internalOutletUsers.forEach((user) => {
            const outletLabel = user?.outlet_name || user?.outletName || "Other";
            if (!groups[outletLabel]) groups[outletLabel] = [];
            groups[outletLabel].push({
                value: user?._id || user?.id,
                label: `${user?.name || user?.staff_name || "Unnamed"}${user?.mobile_number ? ` · ${user.mobile_number}` : ""}`,
            });
        });
        return Object.entries(groups).map(([outletLabel, options]) => ({
            label: outletLabel,
            options,
        }));
    }, [internalOutletUsers]);

    const renderModalBody = () => {
        if (modalSection === "outlets") {
            return (
                <Form onSubmit={handleSubmit}>
                    {modalMode === "create" && (
                        <>
                            <OutletSelect
                                label="Prefill from existing outlet"
                                placeholder="Search outlets to prefill this form..."
                                value={selectedMainOutlet}
                                onChange={handleSelectMainOutlet}
                                outlets={internalOutlets}
                                isClearable
                                required={false}
                            />
                            <hr className="my-3" />
                        </>
                    )}
                    <Form.Group className="mb-3">
                        <Form.Label>Outlet name</Form.Label>
                        <Form.Control size="sm" value={outletForm.outlet_name} onChange={(e) => setOutletForm({ ...outletForm, outlet_name: e.target.value })} required />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Outlet code</Form.Label>
                        <Form.Control size="sm" value={outletForm.outlet_code} onChange={(e) => setOutletForm({ ...outletForm, outlet_code: e.target.value })} />
                    </Form.Group>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>City</Form.Label>
                                <Form.Control size="sm" value={outletForm.city} onChange={(e) => setOutletForm({ ...outletForm, city: e.target.value })} />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>State</Form.Label>
                                <Form.Control size="sm" value={outletForm.state} onChange={(e) => setOutletForm({ ...outletForm, state: e.target.value })} />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Form.Group className="mb-3">
                        <Form.Label>Address</Form.Label>
                        <Form.Control as="textarea" rows={3} size="sm" value={outletForm.address} onChange={(e) => setOutletForm({ ...outletForm, address: e.target.value })} />
                    </Form.Group>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Latitude</Form.Label>
                                <Form.Control type="number" step="0.000001" size="sm" value={outletForm.latitude} onChange={(e) => setOutletForm({ ...outletForm, latitude: e.target.value })} />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Longitude</Form.Label>
                                <Form.Control type="number" step="0.000001" size="sm" value={outletForm.longitude} onChange={(e) => setOutletForm({ ...outletForm, longitude: e.target.value })} />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Form.Group className="mb-3">
                        <Form.Label>Status</Form.Label>
                        <Form.Select size="sm" value={outletForm.is_active} onChange={(e) => setOutletForm({ ...outletForm, is_active: Number(e.target.value) })}>
                            <option value={1}>Active</option>
                            <option value={0}>Inactive</option>
                        </Form.Select>
                    </Form.Group>
                </Form>
            );
        }

        if (modalSection === "staff") {
            return (
                <Form onSubmit={handleSubmit}>
                    {modalMode === "create" && (
                        <>
                            <Form.Group className="mb-3">
                                <Form.Label className="fw-semibold">
                                    <i className="bi bi-person-badge me-2"></i>Prefill from existing outlet user
                                </Form.Label>
                                <Select
                                    isClearable
                                    isSearchable
                                    placeholder="Search staff/outlet users..."
                                    value={selectedMainStaff}
                                    onChange={handleSelectMainStaff}
                                    options={staffUserOptions}
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    styles={staffSelectStyles}
                                    noOptionsMessage={() => "No outlet users found"}
                                />
                            </Form.Group>
                            <hr className="my-3" />
                        </>
                    )}
                    <Form.Group className="mb-3">
                        <Form.Label>Outlet</Form.Label>
                        <Form.Select size="sm" value={staffForm.outlet_id} onChange={(e) => setStaffForm({ ...staffForm, outlet_id: e.target.value })} required>
                            <option value="">Select outlet</option>
                            {outlets.map((item) => (<option key={item?.id || item?._id} value={item?.id || item?._id}>{item?.outlet_name}</option>))}
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Staff name</Form.Label>
                        <Form.Control size="sm" value={staffForm.staff_name} onChange={(e) => setStaffForm({ ...staffForm, staff_name: e.target.value })} required />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Mobile number</Form.Label>
                        <Form.Control size="sm" value={staffForm.mobile_number} onChange={(e) => setStaffForm({ ...staffForm, mobile_number: e.target.value })} required />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Status</Form.Label>
                        <Form.Select size="sm" value={staffForm.is_active} onChange={(e) => setStaffForm({ ...staffForm, is_active: Number(e.target.value) })}>
                            <option value={1}>Active</option>
                            <option value={0}>Inactive</option>
                        </Form.Select>
                    </Form.Group>
                </Form>
            );
        }

        if (modalSection === "categories") {
            return (
                <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-3">
                        <Form.Label>Category name</Form.Label>
                        <Form.Control size="sm" value={categoryForm.category_name} onChange={(e) => setCategoryForm({ ...categoryForm, category_name: e.target.value })} required />
                    </Form.Group>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Checklist type</Form.Label>
                                <Form.Select size="sm" value={categoryForm.checklist_type} onChange={(e) => setCategoryForm({ ...categoryForm, checklist_type: e.target.value })}>
                                    <option value="OPENING">OPENING</option>
                                    <option value="CLOSING">CLOSING</option>
                                    <option value="GENERAL">GENERAL</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Frequency</Form.Label>
                                <Form.Select size="sm" value={categoryForm.frequency} onChange={(e) => setCategoryForm({ ...categoryForm, frequency: e.target.value })}>
                                    <option value="DAILY">DAILY</option>
                                    <option value="WEEKLY">WEEKLY</option>
                                    <option value="BIWEEKLY">BIWEEKLY</option>
                                    <option value="MONTHLY">MONTHLY</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Sort order</Form.Label>
                                <Form.Control type="number" size="sm" value={categoryForm.sort_order} onChange={(e) => setCategoryForm({ ...categoryForm, sort_order: e.target.value })} />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Status</Form.Label>
                                <Form.Select size="sm" value={categoryForm.is_active} onChange={(e) => setCategoryForm({ ...categoryForm, is_active: Number(e.target.value) })}>
                                    <option value={1}>Active</option>
                                    <option value={0}>Inactive</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </Row>
                </Form>
            );
        }

        if (modalSection === "tasks") {
            return (
                <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-3">
                        <Form.Label>Category</Form.Label>
                        <Form.Select size="sm" value={taskForm.category_id} onChange={(e) => setTaskForm({ ...taskForm, category_id: e.target.value })} required>
                            <option value="">Select category</option>
                            {categories.map((item) => (<option key={item?.id || item?._id} value={item?.id || item?._id}>{item?.category_name}</option>))}
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Task name</Form.Label>
                        <Form.Control size="sm" value={taskForm.task_name} onChange={(e) => setTaskForm({ ...taskForm, task_name: e.target.value })} required />
                    </Form.Group>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Mandatory</Form.Label>
                                <Form.Select size="sm" value={taskForm.is_mandatory} onChange={(e) => setTaskForm({ ...taskForm, is_mandatory: Number(e.target.value) })}>
                                    <option value={1}>Yes</option>
                                    <option value={0}>No</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Status</Form.Label>
                                <Form.Select size="sm" value={taskForm.is_active} onChange={(e) => setTaskForm({ ...taskForm, is_active: Number(e.target.value) })}>
                                    <option value={1}>Active</option>
                                    <option value={0}>Inactive</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </Row>
                    <Form.Group className="mb-3">
                        <Form.Label>Sort order</Form.Label>
                        <Form.Control type="number" size="sm" value={taskForm.sort_order} onChange={(e) => setTaskForm({ ...taskForm, sort_order: e.target.value })} />
                    </Form.Group>
                </Form>
            );
        }

        if (modalSection === "assignments") {
            return (
                <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-3">
                        <Form.Label>Outlet</Form.Label>
                        <Form.Select size="sm" value={assignmentForm.outlet_id} onChange={(e) => setAssignmentForm({ ...assignmentForm, outlet_id: e.target.value })} required>
                            <option value="">Select outlet</option>
                            {outlets.map((item) => (<option key={item?.id || item?._id} value={item?.id || item?._id}>{item?.outlet_name}</option>))}
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Checklist category</Form.Label>
                        <Form.Select size="sm" value={assignmentForm.category_id} onChange={(e) => setAssignmentForm({ ...assignmentForm, category_id: e.target.value })} required>
                            <option value="">Select category</option>
                            {categories.map((item) => (<option key={item?.id || item?._id} value={item?.id || item?._id}>{item?.category_name}</option>))}
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Status</Form.Label>
                        <Form.Select size="sm" value={assignmentForm.is_active} onChange={(e) => setAssignmentForm({ ...assignmentForm, is_active: Number(e.target.value) })}>
                            <option value={1}>Active</option>
                            <option value={0}>Inactive</option>
                        </Form.Select>
                    </Form.Group>
                </Form>
            );
        }

        return null;
    };

    const summaryCards = [
        { title: "Outlets", value: outlets.length },
        { title: "Staff", value: staff.length },
        { title: "Categories", value: categories.length },
        { title: "Tasks", value: tasks.length },
        { title: "Assignments", value: assignments.length },
    ];

    return (
        <React.Fragment>
            <Header />
            <style>{coStyles}</style>
            <div className="main main-app co-page">
                <div className="co-topbar">
                    <div>
                        <div className="co-eyebrow">Checklist operations</div>
                        <h4 className="co-title">Admin</h4>
                        <p className="co-subtitle">Outlets, staff, checklist categories, tasks, and outlet assignments.</p>
                    </div>
                    <div className="d-flex gap-2">
                        <Button variant="dark" size="sm" onClick={() => openCreateModal(activeTab)}>
                            Add {SECTION_LABEL[activeTab]}
                        </Button>
                    </div>
                </div>

                <div className="co-hint">
                    Recommended setup order: outlets → categories &amp; tasks → outlet assignments → staff.
                </div>

                <div className="co-stats">
                    {summaryCards.map((card, idx) => (
                        <React.Fragment key={card.title}>
                            {idx > 0 && <div className="co-stat-divider" />}
                            <div className="co-stat">
                                <div className="co-stat-value">{card.value}</div>
                                <div className="co-stat-label">{card.title}</div>
                            </div>
                        </React.Fragment>
                    ))}
                </div>

                <Card className="co-card">
                    <Card.Body className="bg-white">
                        <div className="co-toolbar">
                            <Tabs activeKey={activeTab} onSelect={(key) => setActiveTab(key)} className="co-tabs">
                                <Tab eventKey="outlets" title="Outlets" />
                                <Tab eventKey="staff" title="Staff" />
                                <Tab eventKey="categories" title="Categories" />
                                <Tab eventKey="tasks" title="Tasks" />
                                <Tab eventKey="assignments" title="Assignments" />
                            </Tabs>

                            <div className="co-filters">
                                <Form.Control size="sm" className="co-search" placeholder="Search" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                                <Form.Select size="sm" className="co-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                                    <option value="all">All status</option>
                                    <option value="1">Active</option>
                                    <option value="0">Inactive</option>
                                </Form.Select>
                                {activeTab === "staff" && (
                                    <Form.Select size="sm" className="co-select" value={staffOutletFilter} onChange={(e) => setStaffOutletFilter(e.target.value)}>
                                        <option value="all">All outlets</option>
                                        {outlets.map((item) => (<option key={item?.id || item?._id} value={item?.id || item?._id}>{item?.outlet_name}</option>))}
                                    </Form.Select>
                                )}
                                {activeTab === "tasks" && (
                                    <Form.Select size="sm" className="co-select" value={taskCategoryFilter} onChange={(e) => setTaskCategoryFilter(e.target.value)}>
                                        <option value="all">All categories</option>
                                        {categories.map((item) => (<option key={item?.id || item?._id} value={item?.id || item?._id}>{item?.category_name}</option>))}
                                    </Form.Select>
                                )}
                            </div>
                        </div>

                        {loading ? (
                            <div className="text-center py-5"><Spinner animation="border" size="sm" /></div>
                        ) : (
                            <>
                                {activeTab === "outlets" && (
                                    filteredOutlets.length === 0 ? (
                                        <div className="co-empty-block">No outlets match the current filters.</div>
                                    ) : (
                                        <>
                                            <div className="co-outlet-grid">
                                                {pagedOutlets.map((outlet) => {
                                                    const outletId = outlet?.id || outlet?._id;
                                                    const outletStaff = staffByOutlet[String(outletId)] || [];
                                                    const outletAssignments = assignmentsByOutletId[String(outletId)] || [];
                                                    const outletTasks = outletAssignments.flatMap(
                                                        (a) => tasksByCategoryId[String(a?.category_id)] || []
                                                    );
                                                    const initial = (outlet?.outlet_name || "?").trim().charAt(0).toUpperCase();

                                                    return (
                                                        <Card key={outletId} className="co-card co-outlet-card">
                                                            <Card.Body>
                                                                <div className="co-outlet-head">
                                                                    <div className="co-outlet-identity">
                                                                        <div className="co-outlet-avatar">{initial}</div>
                                                                        <div className="co-outlet-title">
                                                                            <div className="co-outlet-name" title={outlet?.outlet_name}>{outlet?.outlet_name}</div>
                                                                            <div className="co-outlet-meta">
                                                                                {outlet?.outlet_code || "—"}
                                                                                {[outlet?.city, outlet?.state].filter(Boolean).length > 0 && (
                                                                                    <> · {[outlet?.city, outlet?.state].filter(Boolean).join(", ")}</>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <StatusTag active={outlet?.is_active === '1'} />
                                                                </div>

                                                                <div className="co-outlet-sections">
                                                                    <div className="co-section">
                                                                        <div className="co-section-head">
                                                                            <span>Staff<span className="co-section-count">{outletStaff.length}</span></span>
                                                                            <button type="button" className="co-link" onClick={() => openAddStaffForOutlet(outlet)}>+ Add</button>
                                                                        </div>
                                                                        <div className="co-section-list">
                                                                            {outletStaff.length === 0 && <div className="co-section-empty">No staff assigned</div>}
                                                                            {outletStaff.map((s) => (
                                                                                <div key={s?.id || s?._id} className="co-section-row">
                                                                                    <span>{s?.staff_name}</span>
                                                                                    <span className="co-section-sub">{s?.mobile_number}</span>
                                                                                    <button
                                                                                        type="button"
                                                                                        className="co-row-delete"
                                                                                        title="Remove staff"
                                                                                        onClick={() => requestDelete("staff", s)}
                                                                                    >
                                                                                        ×
                                                                                    </button>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    </div>

                                                                    <div className="co-section">
                                                                        <div className="co-section-head">
                                                                            <span>Assignments<span className="co-section-count">{outletAssignments.length}</span></span>
                                                                            <button type="button" className="co-link" onClick={() => openAddAssignmentForOutlet(outlet)}>+ Assign</button>
                                                                        </div>
                                                                        <div className="co-section-list">
                                                                            {outletAssignments.length === 0 && <div className="co-section-empty">No assignments yet</div>}
                                                                            {outletAssignments.map((a) => {
                                                                                const cat = categoryById[String(a?.category_id)];
                                                                                const detail = [cat?.checklist_type, cat?.frequency].filter(Boolean).join(" · ");
                                                                                return (
                                                                                    <div key={a?.id || a?._id} className="co-section-row">
                                                                                        <span>{a?.category_name || cat?.category_name || "—"}</span>
                                                                                        <span className="co-section-sub">{detail}</span>
                                                                                        <button
                                                                                            type="button"
                                                                                            className="co-row-delete"
                                                                                            title="Remove assignment"
                                                                                            onClick={() => requestDelete("assignments", a)}
                                                                                        >
                                                                                            ×
                                                                                        </button>
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    </div>

                                                                    <div className="co-section">
                                                                        <div className="co-section-head">
                                                                            <span>Tasks<span className="co-section-count">{outletTasks.length}</span></span>
                                                                            <div className="co-section-head-actions">
                                                                                <button type="button" className="co-link" onClick={() => openAddTaskForOutlet(outlet)}>+ Add</button>
                                                                                <span className="co-sep">·</span>
                                                                                <button type="button" className="co-link" onClick={() => setActiveTab("tasks")}>View all</button>
                                                                            </div>
                                                                        </div>
                                                                        <div className="co-section-list co-section-list-tall">
                                                                            {outletTasks.length === 0 && <div className="co-section-empty">No tasks available</div>}
                                                                            {outletTasks.map((t) => {
                                                                                const cat = categoryById[String(t?.category_id)];
                                                                                return (
                                                                                    <div key={t?.id || t?._id} className="co-section-row">
                                                                                        <span>{t?.task_name}</span>
                                                                                        <span className="co-section-sub">
                                                                                            {cat?.category_name || t?.category_name || ""}
                                                                                            {t?.is_mandatory === 1 ? " · Required" : ""}
                                                                                        </span>
                                                                                        <button
                                                                                            type="button"
                                                                                            className="co-row-delete"
                                                                                            title="Remove task"
                                                                                            onClick={() => requestDelete("tasks", t)}
                                                                                        >
                                                                                            ×
                                                                                        </button>
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                <div className="co-outlet-footer">
                                                                    <RowActions
                                                                        isActive={outlet?.is_active === '1'}
                                                                        onEdit={() => openEditModal("outlets", outlet)}
                                                                        onToggle={() => toggleStatus("outlets", outlet)}
                                                                        onDelete={() => requestDelete("outlets", outlet)}
                                                                    />
                                                                </div>
                                                            </Card.Body>
                                                        </Card>
                                                    );
                                                })}
                                            </div>

                                            {filteredOutlets.length > OUTLET_PAGE_SIZE && (
                                                <div className="co-pagination">
                                                    <span className="co-pagination-info">
                                                        Showing {(safeOutletPage - 1) * OUTLET_PAGE_SIZE + 1}
                                                        –{Math.min(safeOutletPage * OUTLET_PAGE_SIZE, filteredOutlets.length)} of {filteredOutlets.length} outlets
                                                    </span>
                                                    <div className="co-pagination-controls">
                                                        <Button
                                                            variant="outline-secondary"
                                                            size="sm"
                                                            disabled={safeOutletPage <= 1}
                                                            onClick={() => setOutletPage((p) => Math.max(1, p - 1))}
                                                        >
                                                            Prev
                                                        </Button>
                                                        <span className="co-pagination-page">Page {safeOutletPage} of {totalOutletPages}</span>
                                                        <Button
                                                            variant="outline-secondary"
                                                            size="sm"
                                                            disabled={safeOutletPage >= totalOutletPages}
                                                            onClick={() => setOutletPage((p) => Math.min(totalOutletPages, p + 1))}
                                                        >
                                                            Next
                                                        </Button>
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    )
                                )}

                                {activeTab === "staff" && (
                                    <Table responsive hover size="sm" className="co-table align-middle">
                                        <thead>
                                            <tr>
                                                <th>Name</th>
                                                <th>Mobile</th>
                                                <th>Outlet</th>
                                                <th>Status</th>
                                                <th className="text-end">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredStaff.length === 0 && (
                                                <tr><td colSpan={5} className="co-empty">No staff match the current filters.</td></tr>
                                            )}
                                            {filteredStaff.map((item) => (
                                                <tr key={item?.id || item?._id}>
                                                    <td className="fw-medium">{item?.staff_name}</td>
                                                    <td>{item?.mobile_number}</td>
                                                    <td>{item?.outlet_name || "—"}</td>
                                                    <td><StatusTag active={item?.is_active === '1'} /></td>
                                                    <td className="text-end">
                                                        <RowActions
                                                            isActive={item?.is_active === '1'}
                                                            onEdit={() => openEditModal("staff", item)}
                                                            onToggle={() => toggleStatus("staff", item)}
                                                            onDelete={() => requestDelete("staff", item)}
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                )}

                                {activeTab === "categories" && (
                                    <Table responsive hover size="sm" className="co-table align-middle">
                                        <thead>
                                            <tr>
                                                <th>Name</th>
                                                <th>Type</th>
                                                <th>Frequency</th>
                                                <th>Status</th>
                                                <th className="text-end">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredCategories.length === 0 && (
                                                <tr><td colSpan={5} className="co-empty">No categories match the current filters.</td></tr>
                                            )}
                                            {filteredCategories.map((item) => (
                                                <tr key={item?.id || item?._id}>
                                                    <td className="fw-medium">{item?.category_name}</td>
                                                    <td>{item?.checklist_type}</td>
                                                    <td>{item?.frequency}</td>
                                                    <td><StatusTag active={item?.is_active === '1'} /></td>
                                                    <td className="text-end">
                                                        <RowActions
                                                            isActive={item?.is_active === '1'}
                                                            onEdit={() => openEditModal("categories", item)}
                                                            onToggle={() => toggleStatus("categories", item)}
                                                            onDelete={() => requestDelete("categories", item)}
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                )}

                                {activeTab === "tasks" && (
                                    <Table responsive hover size="sm" className="co-table align-middle">
                                        <thead>
                                            <tr>
                                                <th>Task</th>
                                                <th>Category</th>
                                                <th>Mandatory</th>
                                                <th>Status</th>
                                                <th className="text-end">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredTasks.length === 0 && (
                                                <tr><td colSpan={5} className="co-empty">No tasks match the current filters.</td></tr>
                                            )}
                                            {filteredTasks.map((item) => (
                                                <tr key={item?.id || item?._id}>
                                                    <td className="fw-medium">{item?.task_name}</td>
                                                    <td>{item?.category_name || "—"}</td>
                                                    <td>{item?.is_mandatory === '1' ? "Yes" : "No"}</td>
                                                    <td><StatusTag active={item?.is_active === '1'} /></td>
                                                    <td className="text-end">
                                                        <RowActions
                                                            isActive={item?.is_active === '1'}
                                                            onEdit={() => openEditModal("tasks", item)}
                                                            onToggle={() => toggleStatus("tasks", item)}
                                                            onDelete={() => requestDelete("tasks", item)}
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                )}

                                {activeTab === "assignments" && (
                                    <Table responsive hover size="sm" className="co-table align-middle">
                                        <thead>
                                            <tr>
                                                <th>Outlet</th>
                                                <th>Category</th>
                                                <th>Status</th>
                                                <th className="text-end">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredAssignments.length === 0 && (
                                                <tr><td colSpan={4} className="co-empty">No assignments match the current filters.</td></tr>
                                            )}
                                            {filteredAssignments.map((item) => (
                                                <tr key={item?.id || item?._id}>
                                                    <td className="fw-medium">{item?.outlet_name || "—"}</td>
                                                    <td>{item?.category_name || "—"}</td>
                                                    <td><StatusTag active={item?.is_active === '1'} /></td>
                                                    <td className="text-end">
                                                        <RowActions
                                                            isActive={item?.is_active === '1'}
                                                            onEdit={() => openEditModal("assignments", item)}
                                                            onToggle={() => toggleStatus("assignments", item)}
                                                            onDelete={() => requestDelete("assignments", item)}
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                )}
                            </>
                        )}
                    </Card.Body>
                </Card>
            </div>

            <Modal show={showModal} onHide={closeModal} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title className="co-modal-title">
                        {modalMode === "edit" ? "Edit" : "Add"} {SECTION_LABEL[modalSection]}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>{renderModalBody()}</Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" size="sm" onClick={closeModal}>Cancel</Button>
                    <Button variant="dark" size="sm" onClick={handleSubmit} disabled={saving}>
                        {saving ? <Spinner animation="border" size="sm" /> : modalMode === "edit" ? "Save changes" : "Create"}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={!!deleteTarget} onHide={cancelDelete} centered size="sm" className="co-delete-modal">
                <Modal.Header closeButton={!deleteBusy}>
                    <Modal.Title className="co-modal-title">Delete {deleteTarget ? SECTION_LABEL[deleteTarget.section] : "item"}?</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="co-delete-text">
                        This will permanently remove <strong>{deleteTarget?.label}</strong>. This action cannot be undone.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" size="sm" onClick={cancelDelete} disabled={deleteBusy}>Cancel</Button>
                    <Button variant="danger" size="sm" onClick={confirmDelete} disabled={deleteBusy}>
                        {deleteBusy ? <Spinner animation="border" size="sm" /> : "Delete"}
                    </Button>
                </Modal.Footer>
            </Modal>
        </React.Fragment>
    );
};

// Premium, flat-design styling with sophisticated minimal aesthetics
const coStyles = `
  .co-page { padding: 24px 28px 40px; }
  .co-eyebrow { font-size: 11px; letter-spacing: 0.08em; text-transform: uppercase; color: #8a8f98; font-weight: 600; margin-bottom: 2px; }
  .co-title { font-weight: 600; color: #1c1f23; margin-bottom: 2px; }
  .co-subtitle { color: #6b7280; font-size: 13.5px; margin: 0; }
  .co-topbar { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; margin-bottom: 14px; flex-wrap: wrap; }
  .co-hint { font-size: 12.5px; color: #6b7280; background: #f9fafb; border: 1px solid #e5e7eb; padding: 10px 14px; margin-bottom: 20px; font-weight: 400; letter-spacing: -0.1px; }
  
  /* Premium stat cards */
  .co-stats { display: flex; align-items: stretch; border: 1px solid #e5e7eb; background: #ffffff; margin-bottom: 20px; overflow: hidden; }
  .co-stat { flex: 1; padding: 16px 20px; background: #ffffff; transition: background-color 0.15s ease; }
  .co-stat:hover { background: #f9fafb; }
  .co-stat-value { font-size: 24px; font-weight: 700; color: #1a1a1a; line-height: 1.2; letter-spacing: -0.5px; }
  .co-stat-label { font-size: 11px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.8px; margin-top: 3px; font-weight: 600; }
  .co-stat-divider { width: 1px; background: #e5e7eb; }
  
  .co-card { border: 1px solid #e5e7eb; border-radius: 4px; box-shadow: none; }
  .co-toolbar { display: flex; justify-content: space-between; align-items: center; gap: 12px; flex-wrap: wrap; margin-bottom: 14px; }
  .co-tabs .nav-link { color: #6b7280; font-size: 13.5px; padding: 6px 12px; border: none; border-bottom: 2px solid transparent; border-radius: 0; }
  .co-tabs .nav-link.active { color: #1c1f23; font-weight: 600; background: transparent; border-bottom: 2px solid #1c1f23; }
  .co-filters { display: flex; gap: 8px; flex-wrap: wrap; }
  .co-search { width: 200px; }
  .co-select { width: 160px; }
  
  /* Premium tables */
  .co-table thead th { font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em; color: #8a8f98; font-weight: 600; border-bottom: 1px solid #e5e7eb; padding-bottom: 8px; }
  .co-table td { font-size: 13.5px; color: #2b2f36; border-color: #edeef0; padding-top: 10px; padding-bottom: 10px; }
  .co-empty { text-align: center; color: #8a8f98; padding: 28px 0 !important; font-size: 13px; }
  
  /* Status indicators */
  .co-status { display: inline-flex; align-items: center; gap: 7px; font-size: 12px; color: #4b5563; font-weight: 500; }
  .co-dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; }
  .co-dot.is-active { background: #059669; box-shadow: 0 0 0 2px rgba(5, 150, 105, 0.15); }
  .co-dot.is-inactive { background: #d1d5db; }
  
  /* Action links */
  .co-row-actions { display: inline-flex; align-items: center; gap: 8px; }
  .co-link { background: none; border: none; padding: 0; font-size: 12px; color: #6b7280; cursor: pointer; font-weight: 500; transition: color 0.15s ease; }
  .co-link:hover { color: #1a1a1a; text-decoration: none; }
  .co-link-danger { color: #dc2626; }
  .co-link-danger:hover { color: #991b1b; }
  .co-sep { color: #d1d5db; font-size: 12px; }
  
  .co-modal-title { font-size: 16px; font-weight: 600; }
  .co-delete-text { font-size: 13px; color: #4b5563; margin: 0; line-height: 1.5; }
  .co-delete-modal .modal-content { border-radius: 4px; }
  .co-empty-block { text-align: center; color: #8a8f98; padding: 40px 0; font-size: 13px; }

  /* Premium outlet cards grid */
  .co-outlet-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(340px, 1fr)); gap: 16px; align-items: start; }
  
  /* Premium outlet card */
  .co-outlet-card { 
    border: 1px solid #e5e7eb; 
    border-radius: 4px; 
    box-shadow: none;
    transition: border-color 0.2s ease, box-shadow 0.2s ease; 
    background: #ffffff;
  }
  .co-outlet-card:hover { 
    border-color: #c4c8cf; 
    box-shadow: 0 1px 3px rgba(0,0,0,0.04);
  }
  .co-outlet-card .card-body { 
    padding: 20px; 
    background: #ffffff;
  }

  .co-outlet-head { 
    display: flex; 
    justify-content: space-between; 
    align-items: flex-start; 
    gap: 12px; 
    padding-bottom: 14px; 
    margin-bottom: 14px; 
    border-bottom: 1px solid #e8eaef; 
  }

  .co-outlet-identity { 
    display: flex; 
    align-items: center; 
    gap: 12px; 
    min-width: 0; 
  }

  .co-outlet-avatar { 
    width: 38px; 
    height: 38px; 
    background: #374151; 
    color: #ffffff; 
    font-size: 14px; 
    font-weight: 600; 
    display: flex; 
    align-items: center; 
    justify-content: center; 
    flex-shrink: 0; 
    letter-spacing: 0.5px;
  }

  .co-outlet-title { min-width: 0; }

  .co-outlet-name { 
    font-size: 15px; 
    font-weight: 600; 
    color: #1a1a1a; 
    overflow: hidden; 
    text-overflow: ellipsis; 
    white-space: nowrap; 
    letter-spacing: -0.2px;
  }

  .co-outlet-meta { 
    font-size: 12px; 
    color: #6b7280; 
    margin-top: 2px; 
    overflow: hidden; 
    text-overflow: ellipsis; 
    white-space: nowrap; 
    font-weight: 400;
  }

  .co-outlet-sections { 
    display: flex; 
    flex-direction: column; 
    gap: 10px; 
  }

  .co-outlet-footer { 
    display: flex; 
    justify-content: flex-end; 
    margin-top: 14px; 
    padding-top: 12px; 
    border-top: 1px solid #e8eaef; 
  }

  /* Premium section styling */
  .co-section { 
    border: 1px solid #e8eaef; 
    background: #ffffff; 
    padding: 10px 12px;
    transition: border-color 0.15s ease;
  }
  .co-section:hover {
    border-color: #d1d5db;
  }

  .co-section-head { 
    display: flex; 
    justify-content: space-between; 
    align-items: center; 
    font-size: 10.5px; 
    font-weight: 700; 
    color: #4b5563; 
    text-transform: uppercase; 
    letter-spacing: 0.8px; 
    margin-bottom: 8px; 
  }

  .co-section-head-actions {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .co-section-head button { 
    text-transform: none; 
    letter-spacing: normal; 
    font-weight: 500; 
    font-size: 12px;
    color: #374151;
  }
  .co-section-head button:hover {
    color: #1a1a1a;
  }

  .co-section-count { 
    font-weight: 500; 
    color: #9ca3af; 
    margin-left: 6px; 
    font-size: 10px;
  }

  .co-section-list { 
    max-height: 86px; 
    overflow-y: auto; 
    display: flex; 
    flex-direction: column; 
    gap: 6px; 

  scrollbar-width: thin;
  scrollbar-color: #b0b0b0 transparent;
  }

  .co-section-list-tall { 
    max-height: 130px; 
  }

  .co-section-row { 
    display: flex; 
    align-items: center;
    gap: 8px; 
    font-size: 12.5px; 
    color: #374151; 
    padding: 2px 0;
  }

  .co-section-row span:first-child { 
    flex: 1 1 auto;
    overflow: hidden; 
    text-overflow: ellipsis; 
    white-space: nowrap; 
    min-width: 0; 
    font-weight: 500;
  }

  .co-section-sub { 
    color: #6b7280; 
    white-space: nowrap; 
    flex-shrink: 0; 
    font-size: 11.5px;
    font-weight: 400;
  }

  .co-row-delete {
    background: none;
    border: none;
    padding: 0;
    margin: 0;
    width: 14px;
    flex-shrink: 0;
    font-size: 15px;
    line-height: 1;
    color: #c1c5cc;
    cursor: pointer;
    text-align: center;
    transition: color 0.15s ease;
  }
  .co-row-delete:hover { color: #dc2626; }

  .co-section-empty { 
    font-size: 12px; 
    color: #9ca3af; 
    font-weight: 400;
    font-style: normal;
  }

  /* Pagination */
  .co-pagination { display: flex; justify-content: space-between; align-items: center; gap: 12px; flex-wrap: wrap; margin-top: 16px; padding-top: 14px; border-top: 1px solid #edeef0; }
  .co-pagination-info { font-size: 12.5px; color: #8a8f98; }
  .co-pagination-controls { display: flex; align-items: center; gap: 10px; }
  .co-pagination-page { font-size: 12.5px; color: #4b5563; }
`;

export default ChecklistOperations;